import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Key, Eye, EyeOff, User, Building2, Shield, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Componente para mostrar credenciales jerárquicas de un cliente
 * Muestra las credenciales del prospecto/persona y de cada empresa asociada
 * Colapsable - inicia plegado por defecto
 */
const HierarchicalCredentials = ({ 
  clientName = 'Cliente',
  clientRut = null,
  clientClaveSii = null,
  clientClaveUnica = null,
  empresas = [],
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [visiblePasswords, setVisiblePasswords] = useState({});

  const togglePasswordVisibility = (key) => {
    setVisiblePasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const maskValue = (value) => {
    if (!value) return '-';
    return '••••••••';
  };

  // Verificar si hay alguna credencial disponible
  const hasAnyCredentials = clientRut || clientClaveSii || clientClaveUnica || 
    empresas?.some(emp => emp.rut || emp.clave_sii || emp.clave_unica);

  const entidadesCount = 1 + (empresas?.length || 0);

  if (!hasAnyCredentials) {
    return (
      <Card className="p-4 border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 text-slate-500">
          <Key className="w-4 h-4" />
          <span className="text-sm">Sin credenciales registradas</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white overflow-hidden" data-testid="hierarchical-credentials">
      {/* Header - Clickeable para expandir/colapsar */}
      <div 
        className="p-3 bg-amber-100/50 border-b border-amber-200 flex items-center justify-between cursor-pointer hover:bg-amber-100/70 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="credentials-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-amber-200">
            <Key className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-amber-700" />
            ) : (
              <ChevronRight className="w-4 h-4 text-amber-700" />
            )}
            <div>
              <h4 className="font-semibold text-amber-900 text-sm">Credenciales</h4>
              <span className="text-xs text-amber-600">
                {entidadesCount} {entidadesCount === 1 ? 'entidad' : 'entidades'}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 rounded bg-amber-200/50 text-amber-700 text-xs">
          <Shield className="w-3 h-3" />
          Acceso Restringido
        </div>
      </div>

      {/* Content - Solo visible cuando está expandido */}
      {isExpanded && (
        <div className="p-3 space-y-2">
          {/* Credenciales del Prospecto/Persona */}
          <div className="p-3 bg-white rounded-lg border border-slate-200 border-l-4 border-l-[#1E3649]" data-testid="credential-person">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-[#1E3649]" />
              <span className="font-medium text-sm text-[#1E3649]">{clientName}</span>
              <Badge className="text-xs bg-slate-100 text-slate-600">Persona</Badge>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <span className="text-slate-500">RUT:</span>
                <span className="ml-2 font-medium">{clientRut || '-'}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Clave SII:</span>
                <span className="ml-1 font-mono">
                  {clientClaveSii ? (visiblePasswords['person_sii'] ? clientClaveSii : maskValue(clientClaveSii)) : '-'}
                </span>
                {clientClaveSii && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); togglePasswordVisibility('person_sii'); }} 
                    className="text-slate-400 hover:text-slate-600 p-0.5"
                    data-testid="toggle-person-sii"
                  >
                    {visiblePasswords['person_sii'] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-slate-500">Clave Única:</span>
                <span className="ml-1 font-mono">
                  {clientClaveUnica ? (visiblePasswords['person_unica'] ? clientClaveUnica : maskValue(clientClaveUnica)) : '-'}
                </span>
                {clientClaveUnica && (
                  <button 
                    onClick={(e) => { e.stopPropagation(); togglePasswordVisibility('person_unica'); }} 
                    className="text-slate-400 hover:text-slate-600 p-0.5"
                    data-testid="toggle-person-unica"
                  >
                    {visiblePasswords['person_unica'] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Credenciales de cada Empresa */}
          {empresas?.map((emp, idx) => (
            <div 
              key={emp.id || idx} 
              className="p-3 bg-white rounded-lg border border-slate-200 border-l-4 border-l-blue-500"
              data-testid={`credential-empresa-${idx}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-sm text-slate-700">{emp.nombre}</span>
                <Badge className="text-xs bg-blue-100 text-blue-600">Empresa</Badge>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <span className="text-slate-500">RUT:</span>
                  <span className="ml-2 font-medium">{emp.rut || '-'}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">Clave SII:</span>
                  <span className="ml-1 font-mono">
                    {emp.clave_sii ? (visiblePasswords[`emp${idx}_sii`] ? emp.clave_sii : maskValue(emp.clave_sii)) : '-'}
                  </span>
                  {emp.clave_sii && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); togglePasswordVisibility(`emp${idx}_sii`); }} 
                      className="text-slate-400 hover:text-slate-600 p-0.5"
                      data-testid={`toggle-emp${idx}-sii`}
                    >
                      {visiblePasswords[`emp${idx}_sii`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500">Clave Única:</span>
                  <span className="ml-1 font-mono">
                    {emp.clave_unica ? (visiblePasswords[`emp${idx}_unica`] ? emp.clave_unica : maskValue(emp.clave_unica)) : '-'}
                  </span>
                  {emp.clave_unica && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); togglePasswordVisibility(`emp${idx}_unica`); }} 
                      className="text-slate-400 hover:text-slate-600 p-0.5"
                      data-testid={`toggle-emp${idx}-unica`}
                    >
                      {visiblePasswords[`emp${idx}_unica`] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default HierarchicalCredentials;
